import { jsx as _jsx } from "react/jsx-runtime";
import UISortTH from 'UIComponents/table/UISortTH';
import styled from 'styled-components';
import { useCallback, useMemo, useRef, useEffect, Fragment } from 'react';
import { makeGetColumnWidth, setWidth } from '../../resizing/redux/widthsSlice';
import { useTableDispatch, useTableSelector } from '../../redux/reduxHooks';
import UIDragHandleHoverContainer from 'UIComponents/draggable/UIDragHandleHoverContainer';
import { getContentComponent } from './getContentComponent';
import { getColumnKey } from '../../table/utils/getColumnKey';
import { TABLE_BORDER_COLOR, TABLE_HEADER_BACKGROUND_COLOR } from 'HubStyleTokens/theme';
import { CELL_BORDER_WIDTH } from '../../table/constants/sizes';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
const ResizableUISortTH = styled(UISortTH).withConfig({
  displayName: "ResizableSortTH__ResizableUISortTH",
  componentId: "ut9cf3-0"
})(["box-shadow:inset 0 -", "px ", ";&:last-of-type{width:auto !important;}"], CELL_BORDER_WIDTH, TABLE_BORDER_COLOR);

const getSortFromDirection = sortDirection => {
  if (sortDirection === -1) {
    return 'ascending';
  }

  if (sortDirection === 1) {
    return 'descending';
  }

  return 'none';
};

const getDirectionFromSort = sort => sort === 'ascending' ? -1 : 1;

const ResizableSortTH = ({
  column,
  sortDirection,
  columnIndex,
  isLastColumn,
  numberOfFrozenColumns,
  onColumnWidthChange,
  onColumnOrderChange,
  onSortChange,
  children
}) => {
  const {
    columnName,
    columnNamespace,
    sortable,
    width: columnWidth,
    minimumColumnWidth
  } = column;
  const columnKey = getColumnKey(columnNamespace, columnName);
  const getColumnWidth = useMemo(() => makeGetColumnWidth(columnKey), [columnKey]);
  const width = useTableSelector(getColumnWidth);
  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);
  const resizableTHStyle = useMemo(() => ({
    width
  }), [width]);
  const stickyResizableTHStyle = useMemo(() => ({
    width,
    backgroundColor: TABLE_HEADER_BACKGROUND_COLOR,
    borderLeft: `1px solid ${TABLE_BORDER_COLOR}`,
    left: '44px',
    position: 'sticky',
    zIndex: 2
  }), [width]);
  const styles = useMemo(() => numberOfFrozenColumns > columnIndex && columnIndex === 0 ? stickyResizableTHStyle : resizableTHStyle, [columnIndex, numberOfFrozenColumns, resizableTHStyle, stickyResizableTHStyle]); // We must use a ref in this callback because handleMouseMove in getContent above
  // captures the first version it sees. We keep this ref in sync with component
  // state via useEffect. This is when we "commit" the new width.

  const onResizeEnd = useCallback(() => {
    onColumnWidthChange({
      columnNamespace,
      columnName,
      nextWidth: widthRef.current
    });
  }, [columnNamespace, columnName, onColumnWidthChange]);
  const dispatch = useTableDispatch();
  const handleResize = useCallback(nextWidth => dispatch(setWidth({
    columnKey,
    width: Math.max(nextWidth, minimumColumnWidth)
  })), [columnKey, dispatch, minimumColumnWidth]);
  const handleSortChange = useCallback(({
    target: {
      value
    }
  }) => onSortChange({
    columnNamespace,
    columnName,
    direction: getDirectionFromSort(value)
  }), [onSortChange, columnName, columnNamespace]);
  const Content = useMemo(() => getContentComponent({
    column,
    originalWidth: columnWidth,
    columnIndex,
    isLastColumn,
    handleResize,
    onResizeEnd,
    onColumnOrderChange
  }), [column, columnIndex, isLastColumn, columnWidth, handleResize, onColumnOrderChange, onResizeEnd]); // This id is part of our external api and should not be changed.
  // It will be used for features like coaching tips and onboarding tours.

  const externalId = `header-${columnNamespace}-${columnName}`; // HACK: These selectors are required for compatibility with onboarding tours
  // and coaching tips. When we've renamed the contact "name" column to it's
  // more permanent placeholder name, we should remove this and PR onboarding
  // tours/coaching tips to use the standardaized selectors.

  const isContact = columnNamespace === CONTACT_TYPE_ID;
  const coachingTipsSelectors = isContact && columnName === 'name' ? {
    'data-coaching-tips': 'header-name'
  } : {};
  const onboardingSelectors = isContact ? {
    'data-onboarding': `crm-table-header-cell-${columnName}`
  } : {};
  return /*#__PURE__*/_jsx(UIDragHandleHoverContainer, {
    children: /*#__PURE__*/_jsx(ResizableUISortTH, Object.assign({}, coachingTipsSelectors, {}, onboardingSelectors, {
      "data-table-external-id": externalId,
      "data-test-id": externalId,
      "data-test-sort-direction": sortDirection,
      className: "p-all-0",
      role: "columnheader",
      disabled: !sortable,
      style: styles,
      FlexContainer: Fragment,
      Content: Content,
      sort: getSortFromDirection(sortDirection),
      onSortChange: handleSortChange,
      children: children
    }))
  });
};

export default ResizableSortTH;